import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ReferenceLineLabel } from '@/components/Charts/ReferenceLines';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataFromGraphFact, minMaxAxisDomain, sortChartData } from '@/components/Charts/utils';
import { printLatestDate } from '@/components/NCCard/NCCard';
import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
  // MethodologyLink,
} from '@/components/NCExplainers/Explainer';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PlotIndicatorCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotIndicatorCard';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';

const CHART_COLORS = {
  bar: '#B9A4D5',
  trend: '#9C90B0',
  benchmark: '#9E9E9E',
} as const;

const CHART_FACT_NAMES_MAP: Record<string, string> = {
  indicator_species: 'indicatorSpecies',
};

export const IndicatorSpecies = ({ sourceRef }: { sourceRef: string }) => {
  const { t } = useTranslation();

  return (
    // eslint-disable-next-line sonarjs/no-duplicate-string
    <PlotCardWrapper name={t('shared.ncaDetail.details.indicatorSpecies.labels.heading')}>
      <IndicatorSpeciesCard sourceRef={sourceRef} />
    </PlotCardWrapper>
  );
};

const IndicatorSpeciesCard = ({ sourceRef }: { sourceRef: string }) => {
  const { t } = useTranslation();

  const factNames = {
    latest: 'indicator_species',
    benchmark: 'indicator_species',
    baseline: 'indicator_species',
    graph: 'indicator_species',
  };

  const facts = usePlotFacts<number>(Object.values(factNames), undefined, undefined, undefined, sourceRef, {
    start_date_inclusive: '2017-12-31',
    end_date_inclusive: '2026-12-31',
  });
  const graphFact = getFactsFromFactArray<GraphFact[]>(facts, factNames.graph) as GraphFact[] | undefined | null;
  const latestFact = getFactFromFactArray<number>(facts, factNames.latest) as PlotReportFactElement<number> | undefined;
  const baselineFact = graphFact ? (graphFact.at(-2) as PlotReportFactElement<number> | undefined) : undefined;

  const highBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const moderateBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const lowBenchmarkFact = getFactFromFactArray<number>(facts, factNames.benchmark, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });

  const benchmarks = [lowBenchmarkFact?.value, moderateBenchmarkFact?.value, highBenchmarkFact?.value];

  const benchmarkTypes = [BenchmarkType.low, BenchmarkType.moderate, BenchmarkType.high];
  const benchmark = useBenchmarkForFact(benchmarks, latestFact?.value, benchmarkTypes);

  const hasData = !latestFact || isNullish(latestFact.value) || typeof latestFact.value !== 'number';
  if (hasData) {
    return null;
  }
  const cardProps = {
    'data-testid': 'index-card-nca',
    explainer: <IndicatorSpeciesExplainer />,
  };

  const TITLE = t(`shared.ncaDetail.details.indicatorSpecies.labels.heading`);

  const headerProps = {
    title: TITLE,
    className: 'lg:grid-cols-[1fr_1fr]',
  };
  const footerProps = {
    logos: ['tnfd', 'gri', 'sbtn', 'cdp', 'csrd', 'cbd', 'npi', 'sai'],
  };

  const statProps = {
    children: <LatestFacts baselineFact={baselineFact} latestFact={latestFact} />,
    benchmark,
    explainer: benchmark ? (
      <span className='typography-body1'>
        {t(
          `shared.ncaDetail.details.indicatorSpecies.explainer.whatDoesThisMean.benchmarks.${benchmark as 'high' | 'moderate' | 'low'}`,
        )}
      </span>
    ) : null,
  };

  const graphHeaderProps = {
    heading: TITLE,
    popover: {
      title: t('shared.ncaDetail.details.indicatorSpecies.explainer.indicatorSpecies.title'),
      body: t('shared.ncaDetail.details.indicatorSpecies.explainer.indicatorSpecies.body'),
    },
    className: 'w-full px-6 sm:px-0',
  };

  const graphProps = {
    children: <GraphTile graphFact={graphFact} lowBenchmark={lowBenchmarkFact?.value} />,
    graphHeaderProps,
  };

  return (
    <PlotIndicatorCard
      cardProps={cardProps}
      headerProps={headerProps}
      footerProps={footerProps}
      statProps={statProps}
      graphProps={graphProps}
    />
  );
};

const LatestFacts = ({
  baselineFact,
  latestFact,
}: {
  baselineFact: PlotReportFactElement<number> | undefined;
  latestFact: PlotReportFactElement<number> | undefined;
}) => {
  const { t } = useTranslation();

  const latestFactDate = printLatestDate([latestFact?.measured_at]);

  return (
    <Stack className='flex flex-row justify-between items-center'>
      <NCFactCapsule
        label={t('shared.ncaDetail.details.labels.latestValue')}
        fact={{ ...latestFact, unit: UnitEnum.none } as PlotReportFactElement<number>}
        capsuleIconProps={{
          icon: getNCChangeIcon(latestFact),
          variant: getNCChangeIconVariant(latestFact),
        }}
        infoPopoverProps={{
          title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
          body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: latestFactDate }),
          popoverTriggerProps: {
            className: 'text-xs',
          },
        }}
      />
      <NCFactCapsule
        label={t('shared.ncaDetail.details.labels.oneYearChange')}
        fact={{ ...baselineFact, unit: UnitEnum.none } as PlotReportFactElement<number>}
        capsuleIconProps={{
          icon: getNCChangeIcon(baselineFact),
          variant: getNCChangeIconVariant(baselineFact),
        }}
        infoPopoverProps={{
          title: t('shared.ncaDetail.details.carbon.explainers.oneYearChange.title'),
        }}
      />
    </Stack>
  );
};

const GraphTile = ({
  graphFact,
  lowBenchmark,
}: {
  graphFact: GraphFact[] | undefined | null;
  lowBenchmark?: number | null;
}) => {
  const { t } = useTranslation();

  if (!graphFact?.length) return null;

  const dateDataMap = getChartDataFromGraphFact(graphFact, CHART_FACT_NAMES_MAP);
  const sortedData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const chartData = sortedData.map((item) => ({
    ...item,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    indicatorSpeciesTrend: (item as any).indicatorSpecies,
    // TODO: MVP-9692 based on type of benchmarks
    ...(!isNullish(lowBenchmark) && { lowBenchmark }),
  }));

  const legendItems: ChartLegendBarItem[] = [
    {
      label: t('shared.ncaDetail.details.indicatorSpecies.labels.heading'),
      color: CHART_COLORS.bar,
      shape: 'column',
    },
    {
      label: t('shared.ncaDetail.details.labels.trend'),
      color: CHART_COLORS.trend,
      shape: 'line',
    },
  ];

  const widgetConfiguration = {
    name: 'indicator_species_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      'data-testid': 'indicator-species-graph-chart',
      data: chartData,
      chartConfig: [
        {
          shape: ChartShape.bar,
          dataKey: 'indicatorSpecies.value',

          label: t('shared.ncaDetail.details.indicatorSpecies.labels.heading'),
          color: CHART_COLORS.bar,
          fill: CHART_COLORS.bar,
          key: 'indicatorSpecies',
          id: 'indicatorSpecies',
          barSize: 20,
          barColors: [CHART_COLORS.bar],
        },
        {
          shape: ChartShape.line,
          dataKey: 'indicatorSpeciesTrend.value',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (props: any) =>
            props.index === chartData.length - 1 && (
              <ReferenceLineLabel label={t('shared.ncaDetail.details.labels.trend')} {...props} />
            ),
          color: CHART_COLORS.trend,
          key: 'indicatorSpeciesTrend',
          id: 'indicatorSpeciesTrend',
          dot: { stroke: CHART_COLORS.trend, fill: '#474D45' },
          isAnimationActive: false,
          connectNulls: true,
          hideInTooltip: true,
        },
        ...(!isNullish(lowBenchmark)
          ? [
              {
                shape: ChartShape.line,
                dataKey: 'lowBenchmark',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                label: (props: any) =>
                  props.index === chartData.length - 1 && (
                    <ReferenceLineLabel label={t('global.benchmark.low')} {...props} />
                  ),
                color: CHART_COLORS.benchmark,
                key: 'lowBenchmark',
                id: 'lowBenchmark',
                dot: false,
                strokeDasharray: '4 4',
                isAnimationActive: false,
                hideInTooltip: true,
              },
            ]
          : []),
      ],
      legendItems,
      xAxisProps: {
        dataKey: 'label',
        type: 'category',
        tickFormatter: printYear,
      },
      yAxisProps: {
        domain: minMaxAxisDomain(0.98, 1.05),
      },
    },
  };

  return <WidgetSlotRenderer nestedWidget={widgetConfiguration} />;
};

const IndicatorSpeciesExplainer = () => {
  const { t } = useTranslation();

  return (
    <Explainer>
      <ExplainerTrigger data-testid='indicator-species-explainer'>{t('global.howDoWeMeasureThis')}</ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>
          {t(`shared.ncaDetail.details.indicatorSpecies.explainer.howDoWeMeasureThis.title`)}
        </ExplainerTitle>
        <Stack className='items-start pb-6'>
          <ExplainerBody>
            <ExplainerCopy body={t(`shared.ncaDetail.details.indicatorSpecies.explainer.howDoWeMeasureThis.body`)} />
          </ExplainerBody>
          {/* TODO: MVP-9692 update methodology link to correct page when available */}
          {/* <MethodologyLink to={''} /> */}
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};
